/**
 * Custom pointer-based drag for dragging a panel/menu tile onto the poster canvas.
 *
 * Native HTML5 drag-and-drop can't give the UX we want (it paints its own ghost with the tile background,
 * shows a "copy" + badge, and forces the OS arrow cursor — CSS cursors are ignored mid-drag). So this drives
 * the drag with pointer events: a floating clone of the thumbnail follows the cursor, the body cursor is a
 * closed hand the whole time, and on release the whiteboard resolves whether the drop landed on the canvas.
 *
 * The drop target (whiteboard) registers a resolver here; the drag source (a grid/carousel tile, a text menu
 * tile, …) starts a drag. Mirrors the `stock-media-popup-controller` singleton pattern.
 */

import type {DragEvent as ReactDragEvent, PointerEvent as ReactPointerEvent} from 'react';
import type {ItemCoordinates, ItemData} from '@PosterWhiteboard/page/add-item.class';

export type CanvasDropPosition = ItemCoordinates;

type CanvasDropHandler = (point: CanvasDropPosition) => void;
export type CanvasDropResolver = (e: PointerEvent) => CanvasDropPosition | null;

export interface BeginTileDragOptions {
  event: ReactPointerEvent<HTMLElement>;
  onDropOnCanvas: CanvasDropHandler;
  /**
   * Clone the whole tile as the drag ghost instead of just its inner `<img>`. Media tiles (image/video/sticker)
   * are essentially the image, so the bare-img ghost is right for them; audio tiles are rich cards (thumbnail +
   * title + duration + waveform), so they need the full element to look the same while dragging. When the source
   * is an `<li>`, the full-element clone also strips the list-marker bullet and the tile's own square background
   * so only the card shows — a no-op for non-list tiles, which keep their own background/radius.
   */
  cloneFullElement?: boolean;
}

interface TileDragPropsOptions extends Omit<BeginTileDragOptions, 'event'> {
  enableDragToCanvas: boolean;
}

/** The drag-related DOM props a tile spreads onto its `ClickableDiv`. Empty when the tile isn't drag-enabled. */
interface TileDragProps {
  onPointerDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  onDragStart?: (event: ReactDragEvent) => void;
}

const DRAG_START_THRESHOLD = 5;
const CLONE_OPACITY = '0.85';
const CLONE_Z_INDEX = '2147483646';
/** Matches the audio card's `radius-12` corner so the full-element ghost is clipped to the same rounded shape. */
const CLONE_FULL_CORNER_RADIUS = '12px';
const GRABBING_CURSOR_STYLE_ID = 'pmw-canvas-item-drag-cursor';

let dropResolver: CanvasDropResolver | null = null;

/** Forces the grabbing cursor everywhere for the duration of the drag — a global `!important` rule beats per-element cursors (canvas, buttons, etc.). */
const setGlobalGrabbingCursor = (): void => {
  if (document.getElementById(GRABBING_CURSOR_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = GRABBING_CURSOR_STYLE_ID;
  style.textContent = '* { cursor: grabbing !important; }';
  document.head.appendChild(style);
};

const clearGlobalGrabbingCursor = (): void => {
  document.getElementById(GRABBING_CURSOR_STYLE_ID)?.remove();
};

/** Anchors an item at the point it was dropped. An item that was clicked rather than dragged keeps the page's default placement. */
export function placeItemAtDropPosition<T extends ItemData>(item: T, dropPosition?: CanvasDropPosition): T {
  if (!dropPosition) {
    return item;
  }
  return {...item, left: dropPosition.left, top: dropPosition.top};
}

/** Called by the whiteboard. Returns the poster-space point for a release over the canvas, else null. */
export function registerCanvasDropResolver(resolver: CanvasDropResolver): () => void {
  dropResolver = resolver;
  return (): void => {
    if (dropResolver === resolver) {
      dropResolver = null;
    }
  };
}

const createDragClone = (sourceEl: HTMLElement, rect: DOMRect, cloneFullElement: boolean): HTMLElement => {
  // data-canvas-drag-thumb marks the tile's designated thumb element (e.g. the shape tile's recolored <img>).
  const thumb = sourceEl.querySelector<HTMLElement>('img, [data-canvas-drag-thumb]');
  const source = cloneFullElement ? sourceEl : (thumb ?? sourceEl);
  const clone = source.cloneNode(true) as HTMLElement;
  clone.removeAttribute('draggable');
  // The ghost is repositioned on every pointermove, so any inherited transition (tiles carry
  // `hover-transition-all`, which animates `all`) would ease each step and leave it trailing the cursor.
  clone.style.animation = 'none';
  clone.style.transition = 'none';
  clone.style.height = `${rect.height}px`;
  clone.style.left = `${rect.left}px`;
  clone.style.margin = '0';
  clone.style.opacity = CLONE_OPACITY;
  clone.style.pointerEvents = 'none';
  clone.style.position = 'fixed';
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.zIndex = CLONE_Z_INDEX;
  if (!cloneFullElement) {
    clone.style.objectFit = 'contain';
    return clone;
  }
  if (source.tagName === 'LI') {
    // The source tile is an `<li>`; cloned out of its `<ul>` it regains the list-marker bullet and carries its
    // own square tile background. Strip the marker, drop the tile background, and clip to the card's rounded
    // corners so only the rounded card shows. Non-list tiles (e.g. text tiles, `<div role="button">` with their
    // own gray-0 background + radius-8) keep their own styling untouched.
    clone.style.background = 'transparent';
    clone.style.borderRadius = CLONE_FULL_CORNER_RADIUS;
    clone.style.listStyle = 'none';
    clone.style.overflow = 'hidden';
  }
  return clone;
};

/**
 * Swallows the `click` the browser fires after a drag's `pointerup`. Installed only once the drop handler has
 * run, because a handler may dispatch its own click to dismiss the menu it was dragged out of — that one has to
 * get through, and the browser's trailing click is the one that must not reach the canvas.
 */
const suppressNextClick = (): void => {
  const block = (ev: MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  document.addEventListener('click', block, {capture: true, once: true});
  window.setTimeout(() => {
    document.removeEventListener('click', block, true);
  }, 0);
};

/**
 * The `onPointerDown` entry point every draggable tile uses. It only starts *tracking* a potential drag: the drag
 * itself "begins" (clone + grabbing cursor) once the pointer moves past a small threshold, so a plain click still
 * adds the item the normal way. Right- and middle-button presses fall through untouched so they still reach their
 * own menus.
 */
export function beginTileDrag({event, onDropOnCanvas, cloneFullElement = false}: BeginTileDragOptions): void {
  if (event.button !== 0) {
    return;
  }
  const sourceEl = event.currentTarget;
  const startClientX = event.clientX;
  const startClientY = event.clientY;
  const rect = sourceEl.getBoundingClientRect();
  const offsetX = startClientX - rect.left;
  const offsetY = startClientY - rect.top;
  let started = false;
  let clone: HTMLElement | null = null;

  const positionClone = (x: number, y: number): void => {
    if (clone) {
      clone.style.left = `${x - offsetX}px`;
      clone.style.top = `${y - offsetY}px`;
    }
  };

  const cleanup = (): void => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', cleanup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    clearGlobalGrabbingCursor();
    document.body.style.userSelect = '';
  };

  const onMove = (e: PointerEvent): void => {
    if (!started) {
      if (Math.abs(e.clientX - startClientX) < DRAG_START_THRESHOLD && Math.abs(e.clientY - startClientY) < DRAG_START_THRESHOLD) {
        return;
      }
      started = true;
      clone = createDragClone(sourceEl, rect, cloneFullElement);
      document.body.appendChild(clone);
      setGlobalGrabbingCursor();
      document.body.style.userSelect = 'none';
    }
    positionClone(e.clientX, e.clientY);
  };

  const onUp = (e: PointerEvent): void => {
    const wasDragging = started;
    cleanup();
    if (!wasDragging) {
      return;
    }
    const point = dropResolver ? dropResolver(e) : null;
    try {
      if (point) {
        onDropOnCanvas(point);
      }
    } finally {
      suppressNextClick();
    }
  };

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', cleanup);
}

/**
 * Builds the `onPointerDown` handler for a grid tile that adds `item` to the poster, positioned at the drop point
 * when dragged. `shouldCloneFullElement` mirrors `BeginTileDragOptions.cloneFullElement`, decided per item (e.g.
 * audio tiles need the full-card ghost).
 */
export function createGridItemDragHandler<TItem>(
  onDropItem: (item: TItem, dropPosition: CanvasDropPosition) => unknown,
  shouldCloneFullElement?: (item: TItem) => boolean
): (item: TItem, event: ReactPointerEvent<HTMLElement>) => void {
  return (item, event) => {
    beginTileDrag({
      event,
      onDropOnCanvas: (dropPosition) => void onDropItem(item, dropPosition),
      cloneFullElement: shouldCloneFullElement?.(item),
    });
  };
}

/**
 * Builds the drag wiring for a standalone tile (a text preset, a text option, a "do more with text" tile): a
 * left-press starts the canvas drag, and the browser's own image drag is blocked so the two can't run at once.
 */
export function createTileDragProps({enableDragToCanvas, onDropOnCanvas, cloneFullElement}: TileDragPropsOptions): TileDragProps {
  if (!enableDragToCanvas) {
    return {};
  }
  return {
    onPointerDown: (event): void => {
      beginTileDrag({event, onDropOnCanvas, cloneFullElement});
    },
    onDragStart: (event): void => {
      event.preventDefault();
    },
  };
}
